/**
 * DancerLocationService
 *
 * Service for managing dancer location tracking (current and future locations/availability)
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { fetchAllRows } from '@kit/shared/lib/fetch-all-rows';
import { captureServiceError } from '@kit/shared/lib/sentry-helpers';
import { Result, ServiceError } from '@kit/shared/result';
import { BaseService } from '@kit/shared/services';
import type { Database } from '@kit/supabase/database';

import { addLocation, checkForOverlap } from './dancer-location-add';
import {
  mirrorCurrentLocationToProfile,
  upsertCurrentLocation,
} from './dancer-location-upsert';
import type { DancerLocationContext } from './dancer-location.context';
import type {
  CreateLocationInput,
  DancerLocation,
  UpdateLocationInput,
} from './dancer-location.types';

export type {
  CreateLocationInput,
  DancerLocation,
  UpdateLocationInput,
} from './dancer-location.types';

/**
 * Service for managing dancer location tracking
 */
export class DancerLocationService extends BaseService {
  constructor(client: SupabaseClient<Database>) {
    super(client);
  }

  private get ctx(): DancerLocationContext {
    return {
      client: this.client,
      log: (level, message, context) => this.log(level, message, context),
    };
  }

  /**
   * Get all locations for a user, ordered by start_date
   */
  async getLocationsByUser(userId: string): Promise<Result<DancerLocation[]>> {
    const logContext = {
      method: 'getLocationsByUser',
      userId,
    };

    this.log('info', 'Fetching locations for user', logContext);

    try {
      const { data, error } = await fetchAllRows((from, to) =>
        this.client
          .from('artist_locations')
          .select('*')
          .eq('user_id', userId)
          .order('start_date', { ascending: true })
          .order('display_order', { ascending: true })
          .order('id')
          .range(from, to),
      );

      if (error) {
        this.log('error', 'Failed to fetch locations', {
          ...logContext,
          error: error.message,
          errorCode: 'code' in error ? error.code : undefined,
        });
        return Result.fail(ServiceError.database(error.message, error));
      }

      this.log('info', 'Locations fetched successfully', {
        ...logContext,
        count: data?.length || 0,
      });

      return Result.ok(data || []);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'getLocationsByUser',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * Get current location for a user
   */
  async getCurrentLocation(
    userId: string,
  ): Promise<Result<DancerLocation | null>> {
    const logContext = {
      method: 'getCurrentLocation',
      userId,
    };

    this.log('info', 'Fetching current location', logContext);

    try {
      const { data, error } = await this.client
        .from('artist_locations')
        .select('*')
        .eq('user_id', userId)
        .eq('is_current', true)
        .maybeSingle();

      if (error) {
        this.log('error', 'Failed to fetch current location', {
          ...logContext,
          error: error.message,
          errorCode: error.code,
        });
        return Result.fail(ServiceError.database(error.message, error));
      }

      this.log('info', 'Current location fetched', {
        ...logContext,
        hasLocation: !!data,
      });

      return Result.ok(data);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'getCurrentLocation',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * Get upcoming locations (future start_date)
   */
  async getUpcomingLocations(
    userId: string,
  ): Promise<Result<DancerLocation[]>> {
    const logContext = {
      method: 'getUpcomingLocations',
      userId,
    };

    this.log('info', 'Fetching upcoming locations', logContext);

    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

      const { data, error } = await fetchAllRows((from, to) =>
        this.client
          .from('artist_locations')
          .select('*')
          .eq('user_id', userId)
          .gte('start_date', today)
          .order('start_date', { ascending: true })
          .order('id')
          .range(from, to),
      );

      if (error) {
        this.log('error', 'Failed to fetch upcoming locations', {
          ...logContext,
          error: error.message,
          errorCode: 'code' in error ? error.code : undefined,
        });
        return Result.fail(ServiceError.database(error.message, error));
      }

      this.log('info', 'Upcoming locations fetched', {
        ...logContext,
        count: data?.length || 0,
      });

      return Result.ok(data || []);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'getUpcomingLocations',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * Add a new location
   */
  async addLocation(
    userId: string,
    input: CreateLocationInput,
  ): Promise<Result<DancerLocation>> {
    return addLocation(this.ctx, userId, input);
  }

  /**
   * Best-effort mirror of the dancer's current location into
   * `profiles.{city, country_of_residence}`. Failures are logged but do not
   * surface to the caller — the `artist_locations` row already succeeded
   * and is the source of truth for distance preferences.
   *
   * Uses `country_code` (ISO alpha-2) as the country side. Skips if no
   * country_code was supplied (older Airtable rows can be missing it).
   */
  private async mirrorCurrentLocationToProfile(
    userId: string,
    location: { city: string; country_code: string | null },
  ): Promise<void> {
    return mirrorCurrentLocationToProfile(this.ctx, userId, location);
  }

  /**
   * Upsert the dancer's single canonical current-residence row.
   *
   * Every current-residence edit surface (setup wizard About-You step,
   * `/edit` location section, contract-details address step) routes its
   * selected-city payload — built by `mapResidenceToCanonicalLocation` —
   * through here so the canonical `artist_locations.is_current` row carries
   * `place_id` + coordinates + `country_code`, not merely the free-text
   * `profiles.city` label. See
   * `docs/wip/active/WIP_making_dancer_location_canonical_2026_07_08.md`
   * (BAL-635).
   *
   * Unlike `addLocation`, this NEVER trips the date-overlap guard: a
   * residence edit REPLACES the one current row rather than appending a
   * date-ranged history entry, so re-editing a residence always succeeds.
   *
   * Coordinate/place_id downgrade guard: when the incoming payload lacks
   * coordinates or a place_id (e.g. the contract-details address step, which
   * resolves a mailing address without geometry), the existing values are
   * preserved rather than nulled — a coarser save must never erase a precise
   * pin a richer surface already captured (write-both, never leaving the
   * canonical row stale).
   *
   * Mirrors the result into `profiles.{city, country_of_residence}` (the
   * denormalized label the audition registry + ~10 legacy readers depend on).
   */
  async upsertCurrentLocation(
    userId: string,
    input: CreateLocationInput,
  ): Promise<Result<DancerLocation>> {
    return upsertCurrentLocation(this.ctx, userId, input);
  }

  /**
   * Update an existing location
   */
  async updateLocation(
    locationId: string,
    userId: string,
    input: UpdateLocationInput,
  ): Promise<Result<DancerLocation>> {
    const logContext = {
      method: 'updateLocation',
      locationId,
      userId,
      updates: Object.keys(input),
    };

    this.log('info', 'Updating location', logContext);

    try {
      // Validate dates if provided
      if (input.start_date) {
        const startDate = new Date(input.start_date);
        if (isNaN(startDate.getTime())) {
          return Result.fail(
            ServiceError.validation(
              'Invalid start_date format. Use YYYY-MM-DD',
            ),
          );
        }
      }

      if (input.end_date !== undefined) {
        if (input.end_date !== null) {
          const endDate = new Date(input.end_date);
          if (isNaN(endDate.getTime())) {
            return Result.fail(
              ServiceError.validation(
                'Invalid end_date format. Use YYYY-MM-DD',
              ),
            );
          }
        }
      }

      // Check for overlapping dates if dates are being updated
      if (input.start_date || input.end_date !== undefined) {
        // Fetch current location data
        const { data: currentLocation, error: fetchError } = await this.client
          .from('artist_locations')
          .select('start_date, end_date')
          .eq('id', locationId)
          .eq('user_id', userId)
          .single();

        if (fetchError) {
          if (fetchError.code === 'PGRST116') {
            return Result.fail(ServiceError.notFound('Location not found'));
          }
          return Result.fail(
            ServiceError.database(fetchError.message, fetchError),
          );
        }

        const newStartDate = input.start_date || currentLocation.start_date;
        const newEndDate =
          input.end_date !== undefined
            ? input.end_date
            : currentLocation.end_date;

        const overlapResult = await this.checkForOverlap(
          userId,
          newStartDate,
          newEndDate,
          locationId,
        );

        if (!overlapResult.isSuccess) {
          return Result.fail(overlapResult.getError());
        }

        if (overlapResult.getValue()) {
          return Result.fail(
            ServiceError.validation(
              'Updated dates overlap with another location',
            ),
          );
        }
      }

      const updatePayload: Database['public']['Tables']['artist_locations']['Update'] =
        {};
      if (input.city) updatePayload.city = input.city;
      if (input.country) updatePayload.country = input.country;
      if (input.country_code !== undefined)
        updatePayload.country_code = input.country_code || null;
      if (input.place_id !== undefined)
        updatePayload.place_id = input.place_id || null;
      if (input.start_date) updatePayload.start_date = input.start_date;
      if (input.end_date !== undefined) updatePayload.end_date = input.end_date;
      if (input.latitude !== undefined) updatePayload.latitude = input.latitude;
      if (input.longitude !== undefined)
        updatePayload.longitude = input.longitude;

      const { data, error } = await this.client
        .from('artist_locations')
        .update(updatePayload)
        .eq('id', locationId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return Result.fail(ServiceError.notFound('Location not found'));
        }
        this.log('error', 'Failed to update location', {
          ...logContext,
          error: error.message,
          errorCode: error.code,
        });
        return Result.fail(ServiceError.database(error.message, error));
      }

      this.log('info', 'Location updated successfully', {
        ...logContext,
      });

      return Result.ok(data);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'updateLocation',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * Delete a location
   */
  async deleteLocation(
    locationId: string,
    userId: string,
  ): Promise<Result<void>> {
    const logContext = {
      method: 'deleteLocation',
      locationId,
      userId,
    };

    this.log('info', 'Deleting location', logContext);

    try {
      const { error } = await this.client
        .from('artist_locations')
        .delete()
        .eq('id', locationId)
        .eq('user_id', userId);

      if (error) {
        this.log('error', 'Failed to delete location', {
          ...logContext,
          error: error.message,
          errorCode: error.code,
        });
        return Result.fail(ServiceError.database(error.message, error));
      }

      this.log('info', 'Location deleted successfully', {
        ...logContext,
      });

      return Result.ok(undefined);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'deleteLocation',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * Set a location as current (unsets all other current locations)
   */
  async setCurrentLocation(
    locationId: string,
    userId: string,
  ): Promise<Result<DancerLocation>> {
    const logContext = {
      method: 'setCurrentLocation',
      locationId,
      userId,
    };

    this.log('info', 'Setting current location', logContext);

    try {
      // First, unset all other current locations for this user
      const { error: unsetError } = await this.client
        .from('artist_locations')
        .update({ is_current: false })
        .eq('user_id', userId)
        .eq('is_current', true);

      if (unsetError) {
        this.log('error', 'Failed to unset current locations', {
          ...logContext,
          error: unsetError.message,
        });
        return Result.fail(
          ServiceError.database(unsetError.message, unsetError),
        );
      }

      // Now set the specified location as current
      const { data, error } = await this.client
        .from('artist_locations')
        .update({ is_current: true })
        .eq('id', locationId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return Result.fail(ServiceError.notFound('Location not found'));
        }
        this.log('error', 'Failed to set current location', {
          ...logContext,
          error: error.message,
          errorCode: error.code,
        });
        return Result.fail(ServiceError.database(error.message, error));
      }

      this.log('info', 'Current location set successfully', {
        ...logContext,
      });

      // Mirror the new current location to the profile (see addLocation).
      await this.mirrorCurrentLocationToProfile(userId, {
        city: data.city,
        country_code: data.country_code,
      });

      return Result.ok(data);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'setCurrentLocation',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * Check if dates overlap with existing locations (excluding specified locationId)
   * Returns true if overlap exists
   */
  private async checkForOverlap(
    userId: string,
    startDate: string,
    endDate: string | null,
    excludeLocationId: string | null,
  ): Promise<Result<boolean>> {
    return checkForOverlap(
      this.ctx,
      userId,
      startDate,
      endDate,
      excludeLocationId,
    );
  }
}
